/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { IPerformanceClient, Logger } from "@azure/msal-browser";
import { IExtendedTelemetryItem } from "@microsoft/1ds-analytics-js";
import {
    PerformanceCallbackFunction,
    PerformanceEvent,
    PerformanceEvents,
} from "./TelemetryUtils.js";
import { PerformanceTelemetryReporter } from "./PerformanceTelemetryReporter.js";

export type PerformanceTelemetryEvent = IExtendedTelemetryItem["data"] & {
    name: string;
};
export type TelemetryReporterConfiguration = {
    disabled?: boolean;
    eventsToEmit?: PerformanceEvents[];
    appName?: string;
    appVersion?: string;
    synthetic?: boolean;
};

/**
 * Make sure to propagate new table name to GTAS rules https://identitydivision.visualstudio.com/IDDP/_git/msal-javascript-1p?path=/.GDPR/Policy/75ae2111-85e0-4898-922d-c242db4a92af/Kusto/Kusto_top1.tasp.json
 * when adding a new entity to miscEvents. Otherwise, it will be automatically marked as "Out of scope" in S360 data catalog inventory.
 */
// Sampled events for perf analysis
const miscEvents: PerformanceEvents[] = [
    PerformanceEvents.InitializeClientApplication,
    PerformanceEvents.LocalStorageUpdated,
];

/**
 * Make sure to propagate new table name to GTAS rules https://identitydivision.visualstudio.com/IDDP/_git/msal-javascript-1p?path=/.GDPR/Policy/75ae2111-85e0-4898-922d-c242db4a92af/Kusto/Kusto_top1.tasp.json
 * when adding a new entity to allowedEvents. Otherwise, it will be automatically marked as "Out of scope" in S360 data catalog inventory.
 */
const allowedEvents: Set<PerformanceEvents> = new Set([
    PerformanceEvents.AcquireTokenSilent,
    PerformanceEvents.AcquireTokenByCode,
    PerformanceEvents.AcquireTokenPopup,
    PerformanceEvents.AcquireTokenRedirect,
    PerformanceEvents.AcquireTokenPreRedirect,
    PerformanceEvents.PairwiseBrokerApplicationInitializeBrokering,
    PerformanceEvents.SsoSilent,
    // Embedded events
    PerformanceEvents.EmbeddedClientApplicationSendSSOSilentRequest,
    PerformanceEvents.EmbeddedClientApplicationSendPopupRequest,
    PerformanceEvents.EmbeddedClientApplicationSendRedirectRequest,
    PerformanceEvents.EmbeddedClientApplicationHandleRedirectRequest,
    PerformanceEvents.EmbeddedClientApplicationSendSilentRefreshRequest,
    ...miscEvents,
]);

export class TelemetryReporter {
    private perfTelemetryReporter: PerformanceTelemetryReporter;
    private logger: Logger;

    constructor(
        clientId: string,
        logger: Logger,
        performanceClient: IPerformanceClient,
        channelId: string,
        configuration?: TelemetryReporterConfiguration
    ) {
        this.logger = logger;

        const eventsToEmit: PerformanceEvents[] =
            configuration?.eventsToEmit?.filter((event) =>
                allowedEvents?.has(event)
            ) || [...allowedEvents];

        this.perfTelemetryReporter = new PerformanceTelemetryReporter(
            clientId,
            logger,
            performanceClient,
            eventsToEmit,
            miscEvents,
            channelId,
            configuration?.synthetic,
            configuration?.appName,
            configuration?.appVersion
        );
    }

    private sendCustomEvent(event: PerformanceEvent): void {
        this.perfTelemetryReporter.sendCustomEvent(event);
    }

    getTelemetryCallback(): PerformanceCallbackFunction {
        this.logger.trace(
            "TelemetryReporter: Attaching callback to send custom metrics."
        );
        const telemetryCallback: PerformanceCallbackFunction = (
            events: PerformanceEvent[]
        ) => {
            events.forEach((event) => {
                /*
                 * Set proper event name for broker event such that instead of grouping all broker events under the same table
                 * they are placed in the corresponding tables based on the underlying API names
                 * e.g. acquireTokenByBroker -> (acquireTokenSilent | ssoSilent | acquireTokenRedirect | acquireTokenPopup)
                 */
                if (event.name === PerformanceEvents.AcquireTokenByBroker) {
                    event.name = event.brokerEventName || event.name;
                    event.isBroker = true;
                    delete event.brokerEventName;
                    this.logger.verbosePii(
                        `TelemetryReporter: Updated broker event ${JSON.stringify(
                            event
                        )}`,
                        event.correlationId
                    );
                }
                this.sendCustomEvent(event);
            });
        };
        this.logger.verbose(
            "TelemetryReporter: Returning callback as telemetry emitted."
        );
        return telemetryCallback;
    }
}
